import React, { Component, Fragment, createRef } from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import MediaQuery from 'react-responsive';
import ColoredButton from 'components/ColoredButton';
import CalendarContainer from 'calendarContainer';
import { AppointmentType } from 'types/appointment';
import { CalendarFilterType } from 'types/filter';
import { CUT_POINT, NUM_DAYS_MOBILE, NUM_DAYS_DESKTOP } from 'config/constants';
import S from 'calendar.scss';
import cx from 'classnames';

class Calendar extends Component {
  constructor(props) {
    super(props);
    this.workroomName = props.workroom && props.workroom.name;
    this.state = {
      showFilterDrawer: false,
      scrolling: false,
      pastBottom: false,
      currentPage: document.URL.split('/')[3],
      currentTab: document.URL.split('/')[5],
    };
    this.calendarRef = createRef();
    this.wrapperRef = createRef();
    this.toggleScrollStatus = this.toggleScrollStatus.bind(this);
  }

  componentDidMount = () => {
    this.setPath();
    if (this.state.currentTab === 'appointments' ||
        this.state.currentPage === 'schedule') {
      window.addEventListener('scroll', this.toggleScrollStatus, false);
    }
  }

  componentWillUnmount = () => window.removeEventListener('scroll', this.toggleScrollStatus, false);

  setPath = () => this.setState({
    currentPage: document.URL.split('/')[3],
    currentTab: document.URL.split('/')[5],
  });

  calendarRef = createRef();

  toggleScrollStatus = () => {
    const { currentPage, currentTab } = this.state;
    const topBasedOnLocation = () => {
      if (currentPage === 'schedule') return 168;
      if (currentTab === 'appointments') return 231;
      return 0;
    };
    const threshold = topBasedOnLocation();
    const calendarHeaderRef = this.calendarRef.current &&
      this.calendarRef.current.calendarHeaderRef.current;
    const topReached = calendarHeaderRef &&
      calendarHeaderRef.getBoundingClientRect().top <= threshold;
    const containerBottom = this.wrapperRef.current
      ? this.wrapperRef.current.getBoundingClientRect().bottom
      : Infinity;
    const pastBottom = !!topReached && containerBottom <= threshold;
    if (window.pageYOffset > 0 && topReached && !pastBottom) {
      this.setState({ scrolling: true, pastBottom: false });
    } else {
      this.setState({ scrolling: false, pastBottom });
    }
  }

  toggleFilterDrawer = () => this.setState({ showFilterDrawer: !this.state.showFilterDrawer });

  render = () => {
    const { dates, date, onFilterChange, filters, title, navigatePast, isCcs,
      navigateFuture, selectable, onSelect, selectedDayStart, selectedDayEnd,
      selectedAssignee, pendingAppointment, headerClassName, filterClassName,
      filterTheme, role, username, isScheduleBoard, onOpeningClick, defaultCalStart,
      applySmartScheduling, relatedPhases, handleDeliveryValidation, phaseType,
      setDelayedField, phaseZip, employableId, updateScheduleFilters, updateFilters,
      handleShowAssessmentSuggestions, phaseSource, phaseEstimatorId,
      phaseInstallerId } = this.props;
    const { currentPage, currentTab, scrolling } = this.state;
    return (<MediaQuery minWidth={CUT_POINT}>{isDesktop => <Fragment>
      {!isScheduleBoard && phaseType === 'Assessment' && role !== 'estimator' && phaseSource !== 'IFI' && <div className={S.overrideBtnContainer}>
        <ColoredButton
          label="Switch to Suggestion View"
          color="blue"
          onClick={() => handleShowAssessmentSuggestions(true)}
          disabled={false}
        />
      </div>}
      <div ref={this.wrapperRef}>
        <CalendarContainer
          isDesktop={isDesktop}
          navigatePast={navigatePast}
          navigateFuture={navigateFuture}
          title={title}
          toggleFilters={this.toggleFilterDrawer}
          currentPage={currentPage}
          currentTab={currentTab}
          filters={filters}
          handleJumpDate={this.props.handleJumpDate}
          dates={dates}
          headerClassName={cx(headerClassName, {
            [S.inAppointments]: currentTab === 'appointments',
            [S.inSchedule]: currentPage === 'schedule',
            [S.scrolling]: scrolling,
            [S.noSticky]: this.state.pastBottom || (currentTab !== 'appointments' && currentPage !== 'schedule'),
          })}
          active={this.state.showFilterDrawer}
          onFilterChange={onFilterChange}
          updateScheduleFilters={updateScheduleFilters}
          updateFilters={updateFilters}
          filterClassName={filterClassName}
          onClose={this.toggleFilterDrawer}
          theme={filterTheme}
          isScheduleBoard={isScheduleBoard}
          applySmartScheduling={applySmartScheduling}
          handleDeliveryValidation={handleDeliveryValidation}
          calDates={dates.slice(0, isDesktop ? NUM_DAYS_DESKTOP : NUM_DAYS_MOBILE)}
          startDate={date}
          defaultCalStart={defaultCalStart}
          days={isDesktop ? NUM_DAYS_DESKTOP : NUM_DAYS_MOBILE}
          selectable={selectable}
          selectedDayStart={selectedDayStart}
          selectedDayEnd={selectedDayEnd}
          selectedAssignee={selectedAssignee}
          onSelect={onSelect}
          pendingAppointment={pendingAppointment}
          role={role}
          username={username}
          employableId={employableId}
          onOpeningClick={onOpeningClick}
          relatedPhases={relatedPhases}
          calRef={this.calendarRef}
          setDelayedField={setDelayedField}
          phaseZip={phaseZip}
          phaseType={phaseType}
          phaseSource={phaseSource}
          phaseEstimatorId={phaseEstimatorId}
          phaseInstallerId={phaseInstallerId}
          isCcs={isCcs}
        />
      </div>
    </Fragment>}</MediaQuery>);
  }
}

Calendar.propTypes = {
  title: PropTypes.string,
  role: PropTypes.string,
  username: PropTypes.string,
  employableId: PropTypes.number,
  dates: PropTypes.arrayOf(PropTypes.instanceOf(Moment)).isRequired,
  date: PropTypes.instanceOf(Date).isRequired,
  defaultCalStart: PropTypes.instanceOf(Date),
  onFilterChange: PropTypes.func.isRequired,
  filters: CalendarFilterType,
  navigatePast: PropTypes.func.isRequired,
  navigateFuture: PropTypes.func.isRequired,
  selectable: PropTypes.bool,
  selectedDayStart: PropTypes.instanceOf(Moment),
  selectedDayEnd: PropTypes.instanceOf(Moment),
  selectedAssignee: PropTypes.shape({
    id: PropTypes.number,
    kind: PropTypes.string,
  }),
  onSelect: PropTypes.func,
  pendingAppointment: AppointmentType,
  headerClassName: PropTypes.string,
  filterClassName: PropTypes.string,
  filterTheme: PropTypes.shape({
    RTDrawer: PropTypes.string,
    RTList: PropTypes.string,
    RTOverlay: PropTypes.string,
  }),
  workroom: PropTypes.shape({
    name: PropTypes.string,
  }),
  phaseZip: PropTypes.string,
  phaseType: PropTypes.string,
  phaseSource: PropTypes.string,
  phaseEstimatorId: PropTypes.number,
  phaseInstallerId: PropTypes.number,
  handleJumpDate: PropTypes.func,
  isScheduleBoard: PropTypes.bool,
  onOpeningClick: PropTypes.func,
  applySmartScheduling: PropTypes.bool,
  handleDeliveryValidation: PropTypes.func,
  relatedPhases: PropTypes.array,
  setDelayedField: PropTypes.func,
  isCcs: PropTypes.bool,
  updateScheduleFilters: PropTypes.func,
  updateFilters: PropTypes.func,
  handleShowAssessmentSuggestions: PropTypes.func,
};

Calendar.defaultProps = {
  title: 'Calendar',
  dates: [],
  date: new Date(),
  onFilterChange: () => { },
  filters: {},
  navigatePast: () => { },
  navigateFuture: () => { },
  selectable: false,
  isScheduleBoard: false,
  applySmartScheduling: false,
  relatedPhases: [],
  setDelayedField: () => { },
  phaseZip: '',
  phaseType: '',
  phaseSource: '',
  phaseEstimatorId: null,
  phaseInstallerId: null,
  isCcs: false,
  handleShowAssessmentSuggestions: () => { },
};

export default Calendar;



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/schedule/components/calendar.jsx